/**
 * ActiveModulesIndicator - Indicador visual de los módulos activos de DamyxIA
 * Muestra badges con los módulos que DamyxIA está usando en cada respuesta
 * Al hacer clic abre el panel detallado de módulos
 */
import { useLanguage } from '../context/LanguageContext';
import { Info } from 'lucide-react';

const ActiveModulesIndicator = ({ modules, onOpenPanel }) => {
  const { language } = useLanguage();
  
  if (!modules || modules.length === 0) return null;
  
  const txt = {
    es: {
      title: 'Módulos activos',
      clickForDetails: 'Clic para ver detalles',
    },
    en: {
      title: 'Active modules',
      clickForDetails: 'Click for details',
    }
  };
  const t = txt[language] || txt.es;
  
  // Colores por tipo de módulo
  const moduleColors = {
    'REP': 'bg-purple-500/20 text-purple-300 border-purple-500/30 hover:bg-purple-500/30',
    'ANALYSIS': 'bg-blue-500/20 text-blue-300 border-blue-500/30 hover:bg-blue-500/30',
    'CODE': 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/30',
    'CHART': 'bg-amber-500/20 text-amber-300 border-amber-500/30 hover:bg-amber-500/30',
    'DIAGRAM': 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30 hover:bg-cyan-500/30',
    'IMAGE': 'bg-pink-500/20 text-pink-300 border-pink-500/30 hover:bg-pink-500/30',
    'SEARCH': 'bg-orange-500/20 text-orange-300 border-orange-500/30 hover:bg-orange-500/30',
    'MCEP': 'bg-teal-500/20 text-teal-300 border-teal-500/30 hover:bg-teal-500/30',
  };
  
  return (
    <div 
      className="flex flex-wrap items-center gap-1.5 mt-2 mb-1"
      data-testid="active-modules-indicator"
      title={t.title}
    >
      {modules.map((mod, index) => (
        <button
          key={`${mod.module}-${index}`}
          onClick={onOpenPanel}
          className={`
            flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium
            border backdrop-blur-sm transition-all duration-200
            hover:scale-105 cursor-pointer
            ${moduleColors[mod.module] || 'bg-zinc-500/20 text-zinc-300 border-zinc-500/30 hover:bg-zinc-500/30'}
          `}
          title={`${mod.name}: ${mod.category} - ${t.clickForDetails}`}
          data-testid={`module-badge-${mod.module.toLowerCase()}`}
        >
          <span className="text-sm">{mod.icon}</span>
          <span className="hidden sm:inline">{mod.name}</span>
          <span className="sm:hidden">{mod.category}</span>
        </button>
      ))}
      
      {/* Botón de info para abrir panel */}
      {onOpenPanel && (
        <button
          onClick={onOpenPanel}
          className="flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium
            bg-indigo-500/20 text-indigo-300 border border-indigo-500/30
            hover:bg-indigo-500/30 transition-all duration-200 hover:scale-105"
          title={t.clickForDetails}
          data-testid="open-modules-panel-btn"
        >
          <Info className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">{language === 'es' ? 'Ver detalles' : 'View details'}</span>
        </button>
      )}
    </div>
  );
};

export default ActiveModulesIndicator;
